import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/ui/Modal";
import { Stack } from "../../components/ui/Stack";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Alert } from "../../components/ui/Alert";
import { Spinner } from "../../components/ui/Spinner";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import SearchableDropdown from "../../components/SearchableDropdown";
import Tabs, { Tab } from "../../components/Tabs";

interface ShippingFormProps {
 isOpen: boolean;
 methodId?: string | null;
 onClose: () => void;
 onSuccess: () => void;
}

export const ShippingForm: React.FC<ShippingFormProps> = ({
 isOpen,
 methodId,
 onClose,
 onSuccess,
}) => {
 const { t } = useTranslation();
 const { api } = useApi();
 const { isPluginEnabled } = useStore();
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [locations, setLocations] = useState<any[]>([]);
 const [availableCarriers, setAvailableCarriers] = useState<string[]>([]);
 const [validatingConfig, setValidatingConfig] = useState(false);
 const [configValidationError, setConfigValidationError] = useState<string | null>(null);
 const [activeTabIndex, setActiveTabIndex] = useState(0);
 const [showHelpInstructions, setShowHelpInstructions] = useState(false);
 const [fetchingPackages, setFetchingPackages] = useState(false);
 const [packageFetchError, setPackageFetchError] = useState<string | null>(null);
 
 const isCarrierPluginEnabled = isPluginEnabled("shipping-carrier-integration");
 const [formData, setFormData] = useState({
  name: "",
  description: "",
  type: "standard" as "standard" | "express" | "pickup" | "digital",
  basePrice: 0,
  pricePerKg: undefined as number | undefined,
  freeShippingThreshold: undefined as number | undefined,
  estimatedDays: {
   min: 1,
   max: 7,
  },
  isActive: true,
  isDefault: false,
  locationIds: [] as string[],
  // Carrier integration fields
  carrierProvider: undefined as string | undefined,
     carrierConfig: {
      apiKey: "",
      apiSecret: "",
      username: "",
      password: "",
      accountNumber: "",
      billingNumber: "",
      ekp: "",
      countryCode: "DE", // Default to DPD Germany
      messageLanguage: "de_DE", // Default message language
      isTestMode: true,
      isConfigured: false,
      configuredFields: {} as Record<string, boolean>,
     } as any,
  packages: [] as Array<{
   minWeight: number;
   maxWeight?: number;
   price: number;
  }>,
  defaultPackageIndex: undefined as number | undefined,
  internationalPackages: [] as Array<{
   minWeight: number;
   maxWeight?: number;
   price: number;
  }>,
  defaultInternationalPackageIndex: undefined as number | undefined,
 });


 useEffect(() => {
  if (isOpen) {
   fetchLocations();
   if (isCarrierPluginEnabled) {
    fetchAvailableCarriers();
   }
   if (methodId) {
    fetchMethod();
   } else {
    // Reset form for new shipping method
    setFormData({
     name: "",
     description: "",
     type: "standard" as "standard" | "express" | "pickup" | "digital",
     basePrice: 0,
     pricePerKg: undefined,
     freeShippingThreshold: undefined,
     estimatedDays: {
      min: 1,
      max: 7,
     },
     isActive: true,
     isDefault: false,
     locationIds: [],
     carrierProvider: undefined,
     carrierConfig: {
      apiKey: "",
      apiSecret: "",
      username: "",
      password: "",
      accountNumber: "",
      billingNumber: "",
      ekp: "",
      countryCode: "DE", // Default to DPD Germany
      messageLanguage: "de_DE", // Default message language
      isTestMode: true,
      isConfigured: false,
      configuredFields: {},
     },
     packages: [],
     defaultPackageIndex: undefined,
     internationalPackages: [],
     defaultInternationalPackageIndex: undefined,
    });
    setError(null);
   }
  }
 }, [isOpen, methodId, isCarrierPluginEnabled]);

 const fetchLocations = async () => {
  try {
   const response = await api.get(`/locations/admin`);
   setLocations(response.data.locations || []);
  } catch (err: any) {
   console.error("Failed to fetch locations:", err);
  }
 };

 const fetchAvailableCarriers = async () => {
  try {
   const response = await api.get(`/shipping/admin/carriers`);
   setAvailableCarriers(response.data.carriers || []);
  } catch (err: any) {
   console.error("Failed to fetch carriers:", err);
  }
 };

 // Package management helpers
 const addPackage = () => {
  setFormData({
   ...formData,
   packages: [
    ...(formData.packages || []),
    { minWeight: 0, price: 0 },
   ],
  });
 };

 const updatePackage = (index: number, field: "minWeight" | "maxWeight" | "price", value: number | undefined) => {
  const updatedPackages = [...(formData.packages || [])];
  if (updatedPackages[index]) {
   if (field === "maxWeight" && value === 0) {
    // Treat 0 as undefined for maxWeight (no upper limit)
    updatedPackages[index] = { ...updatedPackages[index], maxWeight: undefined };
   } else {
    updatedPackages[index] = { ...updatedPackages[index], [field]: value };
   }
   setFormData({ ...formData, packages: updatedPackages });
  }
 };

 const removePackage = (index: number) => {
  const updatedPackages = formData.packages?.filter((_, i) => i !== index) || [];
  let newDefaultIndex = formData.defaultPackageIndex;
  if (newDefaultIndex !== undefined && newDefaultIndex === index) {
   newDefaultIndex = undefined;
  } else if (newDefaultIndex !== undefined && newDefaultIndex > index) {
   newDefaultIndex = newDefaultIndex - 1;
  }
  setFormData({
   ...formData,
   packages: updatedPackages,
   defaultPackageIndex: newDefaultIndex,
  });
 };

 const setDefaultPackage = (index: number) => {
  setFormData({ ...formData, defaultPackageIndex: index });
 };

 // International package management helpers
 const addInternationalPackage = () => {
  setFormData({
   ...formData,
   internationalPackages: [
    ...(formData.internationalPackages || []),
    { minWeight: 0, price: 0 },
   ],
  });
 };

 const updateInternationalPackage = (
  index: number,
  field: "minWeight" | "maxWeight" | "price",
  value: number | undefined
 ) => {
  const updatedPackages = [...(formData.internationalPackages || [])];
  if (updatedPackages[index]) {
   if (field === "maxWeight" && value === 0) {
    updatedPackages[index] = { ...updatedPackages[index], maxWeight: undefined };
   } else {
    updatedPackages[index] = { ...updatedPackages[index], [field]: value };
   }
   setFormData({ ...formData, internationalPackages: updatedPackages });
  }
 };

 const removeInternationalPackage = (index: number) => {
  const updatedPackages = formData.internationalPackages?.filter((_, i) => i !== index) || [];
  let newDefaultIndex = formData.defaultInternationalPackageIndex;
  if (newDefaultIndex === index) {
   newDefaultIndex = undefined;
  } else if (newDefaultIndex !== undefined && newDefaultIndex > index) {
   newDefaultIndex = newDefaultIndex - 1;
  }
  setFormData({
   ...formData,
   internationalPackages: updatedPackages,
   defaultInternationalPackageIndex: newDefaultIndex,
  });
 };

 const setDefaultInternationalPackage = (index: number) => {
  setFormData({ ...formData, defaultInternationalPackageIndex: index });
 };


 const fetchMethod = async () => {
  try {
   setFetching(true);
   const response = await api.get(`/shipping/admin`);
   const methods = response.data.methods || [];
   const method = methods.find((m: any) => m._id === methodId);
   if (method) {
    setFormData({
     name: method.name || "",
     description: method.description || "",
     type: method.type || "standard",
     basePrice: method.basePrice || 0,
     pricePerKg: method.pricePerKg,
     freeShippingThreshold: method.freeShippingThreshold,
     estimatedDays: method.estimatedDays || { min: 1, max: 7 },
     isActive: method.isActive !== undefined ? method.isActive : true,
     isDefault: method.isDefault || false,
     locationIds: method.locationIds?.map((id: any) => id.toString()) || [],
     carrierProvider: method.carrierProvider,
     carrierConfig: {
      ...(method.carrierConfig || {}),
      // Preserve configuredFields if it exists from backend
      configuredFields: method.carrierConfig?.configuredFields || {},
      // Preserve non-sensitive fields like carrierId
      carrierId: method.carrierConfig?.carrierId || method.carrierConfig?.selectedCarrier || "",
      selectedCarrier: method.carrierConfig?.selectedCarrier || method.carrierConfig?.carrierId || "",
      // Never fill with *** - always leave empty to prevent accidentally saving ***
      // The placeholder text will show *** to indicate the field is configured
      apiKey: "",
      apiSecret: "",
      username: "",
      password: "",
      accountNumber: "",
      billingNumber: "",
      ekp: "",
      countryCode: method.carrierConfig?.countryCode || "DE",
      messageLanguage: method.carrierConfig?.messageLanguage || "de_DE",
      isTestMode: method.carrierConfig?.isTestMode ?? true,
      isConfigured: method.carrierConfig?.isConfigured ?? false,
     } as any,
     packages: method.packages || [],
     defaultPackageIndex: method.defaultPackageIndex,
     internationalPackages: (method.internationalPackages || []).map((pkg: any) => ({
      minWeight: pkg.minWeight,
      maxWeight: pkg.maxWeight,
      price: pkg.price,
      // Remove countryCodes if present (backward compatibility)
     })),
     defaultInternationalPackageIndex: method.defaultInternationalPackageIndex,
    });
   } else {
    setError(t("admin.shipping.errors.notFound"));
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.shipping.errors.fetch"));
  } finally {
   setFetching(false);
  }
 };

 const handleValidateCarrierConfig = async () => {
  if (!formData.carrierProvider || !formData.carrierConfig) {
    setConfigValidationError("Please select a carrier provider");
    return;
  }

  try {
   setValidatingConfig(true);
   setConfigValidationError(null);
   const response = await api.post("/shipping/admin/carrier/validate", {
    provider: formData.carrierProvider,
    config: formData.carrierConfig,
   });

   if (!response.data.valid) {
    setConfigValidationError(response.data.error || "Configuration validation failed");
   } else {
    setConfigValidationError(null);
   }
  } catch (err: any) {
   setConfigValidationError(err.response?.data?.error || "Validation failed");
  } finally {
   setValidatingConfig(false);
  }
 };

 const handleFetchPackages = async () => {
  if (!formData.carrierProvider || formData.carrierProvider !== "fleetrunnr") {
    setPackageFetchError("Package fetching is only available for FleetRunnr");
    return;
  }

  if (!methodId) {
    setPackageFetchError("Please save the shipping method first before fetching packages");
    return;
  }

  try {
   setFetchingPackages(true);
   setPackageFetchError(null);

   const response = await api.post(`/shipping/admin/${methodId}/carrier/packages`);

   // Handle packages response (for all carriers including FleetRunnr)
   const fetchedPackages = response.data.packages || [];
   const basePrice = response.data.basePrice;

   if (fetchedPackages.length === 0) {
    setPackageFetchError("No packages found. Please check your carrier configuration and carrier ID.");
    return;
   }

   // Map packages to our package format
   const mappedPackages = fetchedPackages.map((pkg: any) => {
    // Packages from backend already have minWeight and maxWeight in kg
    return {
     minWeight: pkg.minWeight || 0,
     maxWeight: pkg.maxWeight,
     price: pkg.price || 0,
    };
   });

   // Replace existing packages with fetched ones and set basePrice if provided
   setFormData({
    ...formData,
    packages: mappedPackages,
    basePrice: basePrice !== undefined ? basePrice : formData.basePrice,
    defaultPackageIndex: mappedPackages.length > 0 ? 0 : undefined, // Set first package as default
   });
  } catch (err: any) {
   setPackageFetchError(err.response?.data?.error || "Failed to fetch pricing from FleetRunnr");
  } finally {
   setFetchingPackages(false);
  }
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
   setLoading(true);
   setError(null);

   const payload: any = {
    ...formData,
    pricePerKg: formData.pricePerKg || undefined,
    freeShippingThreshold: formData.freeShippingThreshold || undefined,
    locationIds: formData.locationIds,
    isDefault: formData.isDefault,
   };

   // Only include carrier fields if plugin is enabled
   if (isCarrierPluginEnabled) {
    payload.carrierProvider = formData.carrierProvider || undefined;
    // carrierService removed - platform decides the service
    if (formData.carrierProvider && formData.carrierProvider !== "manual") {
     // Filter out any fields with "***" value to prevent overwriting real credentials
     // Only send fields that have actual values (not empty, not "***")
     const cleanedConfig: any = { ...formData.carrierConfig };
     const fieldsToCheck = ["apiKey", "apiSecret", "username", "password", "accountNumber", "billingNumber", "ekp", "messageLanguage", "countryCode"];
     for (const field of fieldsToCheck) {
      if (cleanedConfig[field] === "***" || cleanedConfig[field] === "") {
       // Remove the field if it's "***" or empty - backend should preserve existing values
       delete cleanedConfig[field];
      }
     }
     // Preserve countryCode and messageLanguage even if empty (they have defaults)
     if (formData.carrierConfig?.countryCode !== undefined) {
      cleanedConfig.countryCode = formData.carrierConfig.countryCode;
     }
     if (formData.carrierConfig?.messageLanguage !== undefined) {
      cleanedConfig.messageLanguage = formData.carrierConfig.messageLanguage;
     }
     // Preserve non-sensitive fields like carrierId, selectedCarrier, apiUrl, testApiUrl
     // These can be safely sent even if empty (they're optional)
     if (formData.carrierConfig?.carrierId !== undefined) {
      cleanedConfig.carrierId = formData.carrierConfig.carrierId;
     }
     if (formData.carrierConfig?.selectedCarrier !== undefined) {
      cleanedConfig.selectedCarrier = formData.carrierConfig.selectedCarrier;
     }
     // Remove configuredFields from the payload (it's only for display)
     delete cleanedConfig.configuredFields;
     payload.carrierConfig = cleanedConfig;
    }
    // Include packages and defaultPackageIndex if provided
    if (formData.packages && formData.packages.length > 0) {
     payload.packages = formData.packages;
     if (formData.defaultPackageIndex !== undefined) {
      payload.defaultPackageIndex = formData.defaultPackageIndex;
     }
    }
    // Include international packages if provided
    if (formData.internationalPackages && formData.internationalPackages.length > 0) {
     payload.internationalPackages = formData.internationalPackages;
    }
   }

   if (methodId) {
    await api.put(`/shipping/admin/${methodId}`, payload);
    
    // Update default flag if changed
    if (payload.isDefault) {
     await api.put(`/shipping/admin/${methodId}/default`, { isDefault: true });
    }
   } else {
    await api.post("/shipping/admin", payload);
    
    // Set default flag if needed
    if (payload.isDefault) {
     // Need to get the created method ID - for now, default will be set in backend
    }
   }

   onSuccess();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.shipping.errors.save"));
  } finally {
   setLoading(false);
  }
 };

 if (fetching) {
  return (
   <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={methodId ? t("admin.shipping.editMethod") : t("admin.shipping.newMethod")}
    size="lg"
   >
    <div className="flex justify-center items-center py-8">
     <Spinner size="lg" />
    </div>
   </Modal>
  );
 }

 // Get carrier setup instructions
 const getCarrierInstructions = (provider: string) => {
  if (provider === "dhl") {
   return {
    steps: [
     {
      title: "1. Register for DHL Parcel DE API",
      description: "Sign up for a DHL business account and request API access",
      link: "https://developer.dhl.com/",
      linkText: "DHL Developer Portal",
     },
     {
      title: "2. Get your API Credentials",
      description: "After registration, you'll receive: Client ID (API Key), Client Secret (API Secret), Username, Password, and EKP (10-digit customer number)",
      link: "https://developer.dhl.com/api-reference/parcel-de-shipping-post-parcel-germany-v2",
      linkText: "DHL API Documentation",
     },
     {
      title: "3. Enter Your Credentials",
      description: "Fill in the API Key, API Secret, Username, Password, and EKP in the form below. Use Test Mode to try it out first.",
     },
    ],
   };
  }
  if (provider === "dpd") {
   return {
    steps: [
     {
      title: "1. Register for DPD API",
      description: "Sign up for a DPD business account and request API access. For DPD Germany, you'll need a DELIS account.",
      link: "https://esolutions.dpd.com",
      linkText: "DPD eSolutions Portal",
     },
     {
      title: "2. Get your API Credentials",
      description: "For DPD Germany: You'll receive DELIS-Id (username) and Password. For Baltic countries (LT, LV, EE): You'll receive an API Key.",
      link: "https://esolutions.dpd.com/dokumente/",
      linkText: "DPD API Documentation",
     },
     {
      title: "3. Enter Your Credentials",
      description: "For DPD Germany: Fill in DELIS-Id (username) and Password. Select country code 'DE' for Germany. For Baltic countries: Use API Key and select appropriate country code (LT, LV, or EE).",
     },
     {
      title: "4. Important Notes",
      description: "DPD Germany uses LoginService V2.0 for authentication. Tokens are valid for one business day. ShipmentService for DPD Germany requires SOAP (currently not supported - use legacy Baltic countries API for REST).",
     },
    ],
   };
  }
  if (provider === "gls") {
   return {
    steps: [
     {
      title: "1. Register for GLS API",
      description: "Sign up for a GLS business account and request API access",
      link: "https://gls-group.com/DE/en/parcels/business-customers/online-services",
      linkText: "GLS Business Portal",
     },
     {
      title: "2. Get your API Credentials",
      description: "You'll receive: Username and Password for API access",
      link: "https://gls-group.com/DE/en/parcels/business-customers/online-services",
      linkText: "GLS API Documentation",
     },
     {
      title: "3. Enter Your Credentials",
      description: "Fill in the Username and Password in the form below.",
     },
    ],
   };
  }
  if (provider === "fleetrunnr") {
   return {
    steps: [
     {
      title: "1. Sign up for FleetRunnr",
      description: "Create an account on FleetRunnr to access the multi-carrier shipping platform",
      link: "https://www.fleetrunnr.com",
      linkText: "FleetRunnr Website",
     },
     {
      title: "2. Generate Access Token",
      description: "Log into your FleetRunnr dashboard and generate a new access token. This token is used for API authentication.",
      link: "https://docs.fleetrunnr.com/quickstart",
      linkText: "FleetRunnr Quickstart Guide",
     },
     {
      title: "3. Enter Your Access Token",
      description: "Copy your access token and paste it into the 'Access Token' field below. This is the only required field for FleetRunnr integration.",
     },
     {
      title: "4. Optional: Configure Carrier Selection",
      description: "If you want to use a specific carrier (e.g., DHL, FedEx, UPS), enter the carrier ID. Leave empty to let FleetRunnr automatically select the best carrier.",
      link: "https://docs.fleetrunnr.com/carriers",
      linkText: "FleetRunnr Carriers Documentation",
     },
    ],
   };
  }
  return { steps: [] };
};

 // Define tab content
 const generalTabContent = (
  <Stack spacing="md">
   <Input
    label={t("admin.shipping.name")}
    required
    value={formData.name}
    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
   />

   <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
     {t("admin.shipping.description")}
    </label>
    <textarea
     value={formData.description}
     onChange={(e) =>
      setFormData({ ...formData, description: e.target.value })
     }
     className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
     rows={3}
    />
   </div>

   <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
     {t("admin.shipping.type")}
    </label>
    <SearchableDropdown
     fetchOptions={async (search: string) => {
      const types = [
       { _id: "standard", label: t("admin.shipping.typeStandard") },
       { _id: "express", label: t("admin.shipping.typeExpress") },
       { _id: "pickup", label: t("admin.shipping.typePickup") },
       { _id: "digital", label: t("admin.shipping.typeDigital") },
      ];
      return types.filter((type) =>
       type.label.toLowerCase().includes(search.toLowerCase())
      );
     }}
     placeholder={t("admin.shipping.selectType")}
     value={formData.type}
     getDisplayLabel={(val: string) => {
      const typeMap: Record<string, string> = {
       standard: t("admin.shipping.typeStandard"),
       express: t("admin.shipping.typeExpress"),
       pickup: t("admin.shipping.typePickup"),
       digital: t("admin.shipping.typeDigital"),
      };
      return typeMap[val] || val;
     }}
     onSelect={(option) => {
      setFormData({
       ...formData,
       type: option._id as any,
      });
     }}
    />
   </div>

   {!isCarrierPluginEnabled && (
    <div className="grid grid-cols-2 gap-4">
     <Input
      label={t("admin.shipping.basePrice")}
      type="number"
      step="0.01"
      min="0"
      required
      value={formData.basePrice}
      onChange={(e) =>
       setFormData({
        ...formData,
        basePrice: parseFloat(e.target.value) || 0,
       })
      }
     />

     <Input
      label={t("admin.shipping.pricePerKg")}
      type="number"
      step="0.01"
      min="0"
      value={formData.pricePerKg || ""}
      onChange={(e) =>
       setFormData({
        ...formData,
        pricePerKg: e.target.value ? parseFloat(e.target.value) : undefined,
       })
      }
     />
    </div>
   )}

   <Input
    label={t("admin.shipping.freeShippingThreshold")}
    type="number"
    step="0.01"
    min="0"
    value={formData.freeShippingThreshold || ""}
    onChange={(e) =>
     setFormData({
      ...formData,
      freeShippingThreshold: e.target.value
       ? parseFloat(e.target.value)
       : undefined,
     })
    }
   />

   <div className="grid grid-cols-2 gap-4">
    <Input
     label={t("admin.shipping.estimatedDaysMin")}
     type="number"
     min="0"
     required
     value={formData.estimatedDays.min}
     onChange={(e) =>
      setFormData({
       ...formData,
       estimatedDays: {
        ...formData.estimatedDays,
        min: parseInt(e.target.value) || 0,
       },
      })
     }
    />

    <Input
     label={t("admin.shipping.estimatedDaysMax")}
     type="number"
     min="0"
     required
     value={formData.estimatedDays.max}
     onChange={(e) =>
      setFormData({
       ...formData,
       estimatedDays: {
        ...formData.estimatedDays,
        max: parseInt(e.target.value) || 0,
       },
      })
     }
    />
   </div>

   <div className="space-y-2">
    <label className="flex items-center gap-2">
     <input
      type="checkbox"
      checked={formData.isActive}
      onChange={(e) =>
       setFormData({ ...formData, isActive: e.target.checked })
      }
      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
     />
     <span className="text-sm font-medium text-gray-700">{t("admin.active")}</span>
    </label>
    <label className="flex items-center gap-2">
     <input
      type="checkbox"
      checked={formData.isDefault}
      onChange={(e) =>
       setFormData({ ...formData, isDefault: e.target.checked })
      }
      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
     />
     <span className="text-sm font-medium text-gray-700">{t("admin.shipping.setAsDefault")}</span>
    </label>
   </div>

   <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
     {t("admin.shipping.availableCountries")}
    </label>
    <div className="border border-gray-300 p-3 max-h-48 overflow-y-auto">
     {locations.length === 0 ? (
      <p className="text-sm text-gray-500">{t("admin.shipping.noCountries")}</p>
     ) : (
      <div className="space-y-2">
       {locations.map((location) => (
        <label
         key={location._id}
         className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
        >
         <input
          type="checkbox"
          checked={formData.locationIds.includes(
           typeof location._id === "string" ? location._id : location._id.toString()
          )}
          onChange={(e) => {
           const locationId = typeof location._id === "string" ? location._id : location._id.toString();
           if (e.target.checked) {
            setFormData({
             ...formData,
             locationIds: [...formData.locationIds, locationId],
            });
           } else {
            setFormData({
             ...formData,
             locationIds: formData.locationIds.filter(
              (id) => id !== locationId
             ),
            });
           }
          }}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
         />
         <span className="text-sm text-gray-700">
          {location.country} ({location.countryCode}) - {location.currency}
         </span>
        </label>
       ))}
      </div>
     )}
    </div>
   </div>
  </Stack>
 );

 const carrierTabContent = isCarrierPluginEnabled ? (
  <Stack spacing="md">
   {formData.carrierProvider && formData.carrierProvider !== "manual" && (
    <div className="mb-4">
     <div className="flex items-center gap-2 mb-2">
      <button
       type="button"
       onClick={() => setShowHelpInstructions(!showHelpInstructions)}
       className="text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
      >
       {showHelpInstructions ? "Hide help" : "Help"}
      </button>
     </div>
     {showHelpInstructions && (
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
       <h4 className="text-sm font-semibold text-blue-800 mb-2">
        How to Get Your {formData.carrierProvider.toUpperCase()} Credentials
       </h4>
       <div className="space-y-3 text-sm text-blue-700">
        {getCarrierInstructions(formData.carrierProvider).steps.map((step, idx) => (
         <div key={idx}>
          <p className="font-medium">{step.title}</p>
          <p className="text-blue-600">{step.description}</p>
          {step.link && (
           <a
            href={step.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800 inline-flex items-center gap-1"
           >
            {step.linkText} →
           </a>
          )}
         </div>
        ))}
       </div>
      </div>
     )}
    </div>
   )}

   <div className="space-y-4">
         <div>
          <div className="flex items-center justify-between mb-2">
           <label className="block text-sm font-medium text-gray-700">
            {t("admin.shipping.carrierProvider")}
           </label>
           {formData.carrierProvider && 
            formData.carrierProvider !== "manual" && 
            formData.carrierConfig?.isConfigured && 
            formData.carrierConfig?.configuredFields && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
             ✓ {t("common.configured") || "Configured"}
            </span>
           )}
          </div>
          <SearchableDropdown
           fetchOptions={async (search: string) => {
            const carriers = availableCarriers.map((c) => ({
             _id: c,
             label: c.charAt(0).toUpperCase() + c.slice(1),
            }));
            return carriers.filter((c) =>
             c.label.toLowerCase().includes(search.toLowerCase())
            );
           }}
           placeholder={t("admin.shipping.selectCarrier")}
           value={formData.carrierProvider || ""}
           getDisplayLabel={(val: string) => {
            if (!val) return "";
            return val.charAt(0).toUpperCase() + val.slice(1);
           }}
           onSelect={(option) => {
            setFormData({
             ...formData,
             carrierProvider: option._id,
             // carrierService removed - platform decides the service
            });
           }}
          />
         </div>

         {formData.carrierProvider && formData.carrierProvider !== "manual" && (
          <>
           <div className="space-y-3">
            {/* API Key - Required for all carriers */}
            <Input
             label={
              <span className="flex items-center gap-1">
               {formData.carrierProvider === "fleetrunnr" ? (t("admin.shipping.fleetrunnrAccessToken") || "Access Token") : t("admin.shipping.apiKey")}
               {formData.carrierConfig?.configuredFields?.apiKey && (
                <span className="text-green-600" title="Configured">✓</span>
               )}
              </span>
             }
             type="password"
             value={formData.carrierConfig?.apiKey || ""}
             onChange={(e) =>
              setFormData({
               ...formData,
               carrierConfig: {
                ...formData.carrierConfig,
                apiKey: e.target.value,
               },
              })
             }
             placeholder={formData.carrierConfig?.configuredFields?.apiKey ? "••••••••" : (formData.carrierProvider === "fleetrunnr" ? (t("admin.shipping.fleetrunnrAccessTokenPlaceholder") || "FleetRunnr Access Token") : t("admin.shipping.apiKeyPlaceholder"))}
             helperText={formData.carrierProvider === "fleetrunnr" ? (t("admin.shipping.fleetrunnrAccessTokenHelper") || "Required: Generate an access token from your FleetRunnr dashboard. This is the only required field for FleetRunnr integration. See the quickstart guide for details.") : undefined}
            />

            {/* DHL Parcel DE: requires API Key, API Secret, Username, Password, and EKP */}
            {formData.carrierProvider === "dhl" && (
             <>
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.apiSecret")}
                 {formData.carrierConfig?.configuredFields?.apiSecret && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               type="password"
               value={formData.carrierConfig?.apiSecret || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  apiSecret: e.target.value,
                 },
                })
               }
               placeholder={formData.carrierConfig?.configuredFields?.apiSecret ? "••••••••" : t("admin.shipping.apiSecretPlaceholder")}
              />
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.accountNumber")}
                 {formData.carrierConfig?.configuredFields?.accountNumber && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               required={!formData.carrierConfig?.isTestMode}
               value={formData.carrierConfig?.accountNumber || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  accountNumber: e.target.value,
                 },
                })
               }
               placeholder={formData.carrierConfig?.configuredFields?.accountNumber ? "••••••••" : (t("admin.shipping.dhlEkpPlaceholder") || "1234567890")}
               helperText={formData.carrierConfig?.isTestMode 
                 ? (t("admin.shipping.dhlEkpTestHelper") || "Optional for test mode. Sandbox may work without a real EKP.")
                 : (t("admin.shipping.dhlEkpHelper") || "EKP: 10-digit DHL business customer number (assigned by DHL sales team)")}
               maxLength={10}
              />
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.username")}
                 {formData.carrierConfig?.configuredFields?.username && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               required={!formData.carrierConfig?.isTestMode}
               value={formData.carrierConfig?.username || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  username: e.target.value,
                 },
                })
               }
               placeholder={formData.carrierConfig?.configuredFields?.username ? "••••••••" : t("admin.shipping.usernamePlaceholder")}
               helperText={formData.carrierConfig?.isTestMode ? t("admin.shipping.dhlTestUsernameHelper") || "Test mode uses fixed credentials" : t("admin.shipping.dhlUsernameHelper") || "DHL business account username"}
              />
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.password")}
                 {formData.carrierConfig?.configuredFields?.password && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               type="password"
               required={!formData.carrierConfig?.isTestMode}
               value={formData.carrierConfig?.password || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  password: e.target.value,
                 },
                })
               }
               placeholder={formData.carrierConfig?.configuredFields?.password ? "••••••••" : t("admin.shipping.passwordPlaceholder")}
               helperText={formData.carrierConfig?.isTestMode ? t("admin.shipping.dhlTestPasswordHelper") || "Test mode uses fixed credentials" : t("admin.shipping.dhlPasswordHelper") || "DHL business account password"}
              />
             </>
            )}

            {/* DPD: Different requirements for Germany vs Baltic countries */}
            {formData.carrierProvider === "dpd" && (
             <>
              {/* Country Code - determines which API to use */}
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.countryCode") || "Country Code"}
                 {formData.carrierConfig?.configuredFields?.countryCode && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               value={formData.carrierConfig?.countryCode || "DE"}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  countryCode: e.target.value.toUpperCase(),
                 },
                })
               }
               placeholder="DE, LT, LV, or EE"
               helperText={formData.carrierConfig?.countryCode === "DE" || !formData.carrierConfig?.countryCode
                 ? "DE = DPD Germany (requires DELIS-Id and Password via LoginService)"
                 : "LT/LV/EE = Legacy Baltic countries (requires API Key)"}
               maxLength={2}
              />

              {/* For DPD Germany (DE): DELIS-Id and Password are required */}
              {(formData.carrierConfig?.countryCode === "DE" || !formData.carrierConfig?.countryCode) && (
               <>
                <Input
                 label={
                  <span className="flex items-center gap-1">
                   {t("admin.shipping.username")} (DELIS-Id)
                   {formData.carrierConfig?.configuredFields?.username && (
                    <span className="text-green-600" title="Configured">✓</span>
                   )}
                  </span>
                 }
                 required={!formData.carrierConfig?.isTestMode}
                 value={formData.carrierConfig?.username || ""}
                 onChange={(e) =>
                  setFormData({
                   ...formData,
                   carrierConfig: {
                    ...formData.carrierConfig,
                    username: e.target.value,
                   },
                  })
                 }
                 placeholder={formData.carrierConfig?.configuredFields?.username ? "••••••••" : (t("admin.shipping.dpdDelisIdPlaceholder") || "DELIS-Id (8-10 characters)")}
                 helperText={t("admin.shipping.dpdDelisIdHelper") || "Your DPD DELIS-Id (username) for LoginService authentication"}
                 maxLength={10}
                />
                <Input
                 label={
                  <span className="flex items-center gap-1">
                   {t("admin.shipping.password")}
                   {formData.carrierConfig?.configuredFields?.password && (
                    <span className="text-green-600" title="Configured">✓</span>
                   )}
                  </span>
                 }
                 type="password"
                 required={!formData.carrierConfig?.isTestMode}
                 value={formData.carrierConfig?.password || ""}
                 onChange={(e) =>
                  setFormData({
                   ...formData,
                   carrierConfig: {
                    ...formData.carrierConfig,
                    password: e.target.value,
                   },
                  })
                 }
                 placeholder={formData.carrierConfig?.configuredFields?.password ? "••••••••" : t("admin.shipping.passwordPlaceholder")}
                 helperText={t("admin.shipping.dpdPasswordHelper") || "Your DPD password for LoginService authentication"}
                />
                <Input
                 label={
                  <span className="flex items-center gap-1">
                   {t("admin.shipping.messageLanguage") || "Message Language"}
                   {formData.carrierConfig?.configuredFields?.messageLanguage && (
                    <span className="text-green-600" title="Configured">✓</span>
                   )}
                  </span>
                 }
                 value={formData.carrierConfig?.messageLanguage || "de_DE"}
                 onChange={(e) =>
                  setFormData({
                   ...formData,
                   carrierConfig: {
                    ...formData.carrierConfig,
                    messageLanguage: e.target.value,
                   },
                  })
                 }
                 placeholder="de_DE or en_US"
                 helperText={t("admin.shipping.dpdMessageLanguageHelper") || "Language for API messages: 'de_DE' for German, 'en_US' for English"}
                 maxLength={5}
                />
               </>
              )}

              {/* For Legacy Baltic countries (LT, LV, EE): API Key is required */}
              {formData.carrierConfig?.countryCode &&
               formData.carrierConfig?.countryCode !== "DE" &&
               ["LT", "LV", "EE"].includes(formData.carrierConfig?.countryCode) && (
               <>
                <Input
                 label={
                  <span className="flex items-center gap-1">
                   {t("admin.shipping.username")}
                   {formData.carrierConfig?.configuredFields?.username && (
                    <span className="text-green-600" title="Configured">✓</span>
                   )}
                  </span>
                 }
                 required={!formData.carrierConfig?.isTestMode}
                 value={formData.carrierConfig?.username || ""}
                 onChange={(e) =>
                  setFormData({
                   ...formData,
                   carrierConfig: {
                    ...formData.carrierConfig,
                    username: e.target.value,
                   },
                  })
                 }
                 placeholder={formData.carrierConfig?.configuredFields?.username ? "••••••••" : t("admin.shipping.usernamePlaceholder")}
                />
                <Input
                 label={
                  <span className="flex items-center gap-1">
                   {t("admin.shipping.password")}
                   {formData.carrierConfig?.configuredFields?.password && (
                    <span className="text-green-600" title="Configured">✓</span>
                   )}
                  </span>
                 }
                 type="password"
                 required={!formData.carrierConfig?.isTestMode}
                 value={formData.carrierConfig?.password || ""}
                 onChange={(e) =>
                  setFormData({
                   ...formData,
                   carrierConfig: {
                    ...formData.carrierConfig,
                    password: e.target.value,
                   },
                  })
                 }
                 placeholder={formData.carrierConfig?.configuredFields?.password ? "••••••••" : t("admin.shipping.passwordPlaceholder")}
                />
               </>
              )}
             </>
            )}

            {/* GLS: require username */}
            {formData.carrierProvider === "gls" && (
             <>
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.username")}
                 {formData.carrierConfig?.configuredFields?.username && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               required={!formData.carrierConfig?.isTestMode}
               value={formData.carrierConfig?.username || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  username: e.target.value,
                 },
                })
               }
               placeholder={formData.carrierConfig?.configuredFields?.username ? "••••••••" : t("admin.shipping.usernamePlaceholder")}
              />
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.password")}
                 {formData.carrierConfig?.configuredFields?.password && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               type="password"
               required={!formData.carrierConfig?.isTestMode}
               value={formData.carrierConfig?.password || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  password: e.target.value,
                 },
                })
               }
               placeholder={formData.carrierConfig?.configuredFields?.password ? "••••••••" : t("admin.shipping.passwordPlaceholder")}
              />
             </>
            )}

            {/* Fleetrunnr: only requires Access Token (API Key), optional Carrier ID */}
            {formData.carrierProvider === "fleetrunnr" && (
             <>
              <Input
               label={
                <span className="flex items-center gap-1">
                 {t("admin.shipping.carrierId") || "Carrier ID"}
                 {formData.carrierConfig?.configuredFields?.carrierId && (
                  <span className="text-green-600" title="Configured">✓</span>
                 )}
                </span>
               }
               value={formData.carrierConfig?.carrierId || formData.carrierConfig?.selectedCarrier || ""}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  carrierId: e.target.value,
                  selectedCarrier: e.target.value,
                 },
                })
               }
               placeholder={t("admin.shipping.fleetrunnrCarrierIdPlaceholder") || "e.g., dhl, fedex, ups (optional)"}
               helperText={t("admin.shipping.fleetrunnrCarrierIdHelper") || "Optional: Specify which carrier to use through FleetRunnr. Leave empty to let FleetRunnr automatically select the best carrier for each shipment."}
              />
             </>
            )}

            {/* Test Mode - Available for all carriers except FleetRunnr */}
            {formData.carrierProvider !== "fleetrunnr" && (
             <label className="flex items-center gap-2">
              <input
               type="checkbox"
               checked={formData.carrierConfig?.isTestMode || false}
               onChange={(e) =>
                setFormData({
                 ...formData,
                 carrierConfig: {
                  ...formData.carrierConfig,
                  isTestMode: e.target.checked,
                 },
                })
               }
               className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">{t("admin.shipping.testMode")}</span>
             </label>
            )}

           </div>

           {/* Base Price and Price Per Kg - shown below carrier integration form */}
           <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
             {t("admin.shipping.pricing") || "Pricing"}
            </h4>
            <div className="grid grid-cols-2 gap-4">
             <Input
              label={(() => {
               const baseLabel = t("admin.shipping.basePrice") || "Base Price";
               if (formData.locationIds && formData.locationIds.length > 0) {
                const selectedLocations = locations.filter((loc) =>
                 formData.locationIds.includes(
                  typeof loc._id === "string" ? loc._id : loc._id.toString()
                 )
                );
                if (selectedLocations.length > 0) {
                 const currency = selectedLocations[0].currency || "USD";
                 // Remove existing currency from label if present, then add correct one
                 const labelWithoutCurrency = baseLabel.replace(/\s*\([^)]*\)\s*$/, "").trim();
                 return `${labelWithoutCurrency} (${currency})`;
                }
               }
               return baseLabel;
              })()}
              type="number"
              step="0.01"
              min="0"
              required
              value={formData.basePrice}
              onChange={(e) =>
               setFormData({
                ...formData,
                basePrice: parseFloat(e.target.value) || 0,
               })
              }
             />

             <Input
              label={(() => {
               const baseLabel = t("admin.shipping.pricePerKg") || "Price Per Kg";
               if (formData.locationIds && formData.locationIds.length > 0) {
                const selectedLocations = locations.filter((loc) =>
                 formData.locationIds.includes(
                  typeof loc._id === "string" ? loc._id : loc._id.toString()
                 )
                );
                if (selectedLocations.length > 0) {
                 const currency = selectedLocations[0].currency || "USD";
                 // Remove existing currency from label if present, then add correct one
                 const labelWithoutCurrency = baseLabel.replace(/\s*\([^)]*\)\s*$/, "").trim();
                 return `${labelWithoutCurrency} (${currency})`;
                }
               }
               return baseLabel;
              })()}
              type="number"
              step="0.01"
              min="0"
              value={formData.pricePerKg || ""}
              onChange={(e) =>
               setFormData({
                ...formData,
                pricePerKg: e.target.value ? parseFloat(e.target.value) : undefined,
               })
              }
             />
            </div>
           </div>

           {/* Weight-Based Packages Table */}
           <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
             {t("admin.shipping.weightPackages") || "Weight-Based Packages"}
            </h4>
            <div className="space-y-4">
             <p className="text-sm text-gray-600">
              {t("admin.shipping.packagesDescription") || "Define shipping packages based on weight ranges. The shipping cost will be calculated based on the order weight. If weight cannot be calculated, the default package will be used."}
             </p>

             {/* Fetch Packages Button for FleetRunnr integration */}
             {formData.carrierProvider === "fleetrunnr" && (
              <div className="space-y-2">
               <Button
                type="button"
                variant="outline"
                onClick={handleFetchPackages}
                loading={fetchingPackages}
               >
                {t("admin.shipping.fetchPackages") || "Fetch Packages from FleetRunnr"}
               </Button>
               {packageFetchError && (
                <Alert variant="error" onClose={() => setPackageFetchError(null)}>
                 {packageFetchError}
                </Alert>
               )}
              </div>
             )}

             {/* Packages Table */}
             {formData.packages && formData.packages.length > 0 && (
              <div className="overflow-x-auto">
               <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                 <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.minWeight") || "Min Weight (kg)"}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.maxWeight") || "Max Weight (kg)"}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.price") || "Price"}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.default") || "Default"}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"></th>
                 </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                 {formData.packages.map((pkg, index) => (
                  <tr key={index}>
                   <td className="px-3 py-2">
                    <Input
                     type="number"
                     step="0.01"
                     min="0"
                     value={pkg.minWeight || ""}
                     onChange={(e) => updatePackage(index, "minWeight", parseFloat(e.target.value) || 0)}
                     className="w-24"
                    />
                   </td>
                   <td className="px-3 py-2">
                    <Input
                     type="number"
                     step="0.01"
                     min="0"
                     value={pkg.maxWeight || ""}
                     onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || value === "0") {
                       updatePackage(index, "maxWeight", undefined);
                      } else {
                       updatePackage(index, "maxWeight", parseFloat(value));
                      }
                     }}
                     placeholder={t("admin.shipping.noLimit") || "No limit"}
                     className="w-24"
                    />
                   </td>
                   <td className="px-3 py-2">
                    <Input
                     type="number"
                     step="0.01"
                     min="0"
                     value={pkg.price || ""}
                     onChange={(e) => updatePackage(index, "price", parseFloat(e.target.value) || 0)}
                     className="w-24"
                    />
                   </td>
                   <td className="px-3 py-2">
                    <input
                     type="radio"
                     name="defaultPackage"
                     checked={formData.defaultPackageIndex === index}
                     onChange={() => setDefaultPackage(index)}
                     className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                   </td>
                   <td className="px-3 py-2">
                    <Button
                     type="button"
                     variant="outline"
                     size="sm"
                     onClick={() => removePackage(index)}
                    >
                     {t("common.remove") || "Remove"}
                    </Button>
                   </td>
                  </tr>
                 ))}
                </tbody>
               </table>
              </div>
             )}

             {/* Add Package Button */}
             <Button
              type="button"
              variant="outline"
              onClick={addPackage}
             >
              {t("admin.shipping.addPackage") || "+ Add Package"}
             </Button>
            </div>
           </div>
          </>
         )}

   </div>
  </Stack>
 ) : (
  <Stack spacing="md">
   <Alert variant="warning">
    {t("admin.shipping.pluginNotEnabled")}
   </Alert>
  </Stack>
 );

 const internationalShippingTabContent = (
  <Stack spacing="md">
    <h4 className="text-sm font-semibold text-gray-700 mb-2">
     {t("admin.shipping.internationalPackages") || "International Shipping Packages"}
    </h4>
    <div className="space-y-2">
     <p className="text-sm text-gray-600">
      {t("admin.shipping.internationalPackagesDescription") || "Define shipping packages for international shipping. These packages will automatically apply to all mapped countries from the locations where this shipping method is enabled. When a user visits from a mapped country, these international packages will be used instead of the regular packages."}
     </p>

     {/* International Packages Table */}
     {formData.internationalPackages && formData.internationalPackages.length > 0 && (
      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.minWeight") || "Min Weight (kg)"}</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.maxWeight") || "Max Weight (kg)"}</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.price") || "Price"}</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.shipping.default") || "Default"}</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"></th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {formData.internationalPackages.map((pkg, index) => (
          <tr key={index}>
           <td className="px-3 py-2">
            <Input
             type="number"
             step="0.01"
             min="0"
             value={pkg.minWeight || ""}
             onChange={(e) => updateInternationalPackage(index, "minWeight", parseFloat(e.target.value) || 0)}
             className="w-24"
            />
           </td>
           <td className="px-3 py-2">
            <Input
             type="number"
             step="0.01"
             min="0"
             value={pkg.maxWeight || ""}
             onChange={(e) => {
              const value = e.target.value;
              if (value === "" || value === "0") {
               updateInternationalPackage(index, "maxWeight", undefined);
              } else {
               updateInternationalPackage(index, "maxWeight", parseFloat(value));
              }
             }}
             placeholder={t("admin.shipping.noLimit") || "No limit"}
             className="w-24"
            />
           </td>
           <td className="px-3 py-2">
            <Input
             type="number"
             step="0.01"
             min="0"
             value={pkg.price || ""}
             onChange={(e) => updateInternationalPackage(index, "price", parseFloat(e.target.value) || 0)}
             className="w-24"
            />
           </td>
           <td className="px-3 py-2">
            <input
             type="radio"
             name="defaultInternationalPackage"
             checked={formData.defaultInternationalPackageIndex === index}
             onChange={() => setDefaultInternationalPackage(index)}
             className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
           </td>
           <td className="px-3 py-2">
            <Button
             type="button"
             variant="outline"
             size="sm"
             onClick={() => removeInternationalPackage(index)}
            >
             {t("common.remove") || "Remove"}
            </Button>
           </td>
          </tr>
         ))}
        </tbody>
       </table>
      </div>
     )}

     {/* Add International Package Button */}
     <Button
      type="button"
      variant="outline"
      onClick={addInternationalPackage}
     >
      {t("admin.shipping.addInternationalPackage") || "+ Add International Package"}
     </Button>
    </div>
  </Stack>
 );

 // Build tabs array with header action for integration tab
 const tabs: Tab[] = [
  {
   title: t("admin.shipping.generalSettings") || "General Settings",
   content: generalTabContent,
  },
 ];

 if (isCarrierPluginEnabled) {
  // Show validate button when carrier provider is selected and required fields are entered
  const hasConfigToValidate = formData.carrierProvider &&
    formData.carrierProvider !== "manual" &&
    (
      // For DPD Germany: need username and password
      (formData.carrierProvider === "dpd" && 
       (formData.carrierConfig?.countryCode === "DE" || !formData.carrierConfig?.countryCode) &&
       formData.carrierConfig?.username && 
       formData.carrierConfig?.password) ||
      // For DPD Baltic countries: need API key or username/password
      (formData.carrierProvider === "dpd" && 
       formData.carrierConfig?.countryCode &&
       formData.carrierConfig?.countryCode !== "DE" &&
       (formData.carrierConfig?.apiKey || (formData.carrierConfig?.username && formData.carrierConfig?.password))) ||
      // For other carriers: need API key
      (formData.carrierProvider !== "dpd" && formData.carrierConfig?.apiKey) ||
      // For DHL: need API key and API secret
      (formData.carrierProvider === "dhl" && formData.carrierConfig?.apiKey && formData.carrierConfig?.apiSecret)
    );

  tabs.push({
   title: t("admin.shipping.carrierIntegration") || "Carrier Integration",
   content: carrierTabContent,
   headerAction: hasConfigToValidate ? (
    <Button
     type="button"
     variant="outline"
     size="sm"
     onClick={handleValidateCarrierConfig}
     loading={validatingConfig}
    >
     {t("admin.shipping.validateConfig")}
    </Button>
   ) : undefined,
  });
 }

 // Add international shipping tab (always available)
 tabs.push({
  title: t("admin.shipping.internationalShipping") || "International Shipping",
  content: internationalShippingTabContent,
 });

 // Combine errors for fixed display
 const modalErrors = (error || configValidationError || packageFetchError) ? (
  <Stack spacing="sm">
   {error && (
    <Alert
     variant="error"
     onClose={() => setError(null)}
    >
     {error}
    </Alert>
   )}
   {configValidationError && (
    <Alert
     variant="error"
     onClose={() => setConfigValidationError(null)}
    >
     {configValidationError}
    </Alert>
   )}
   {packageFetchError && (
    <Alert
     variant="error"
     onClose={() => setPackageFetchError(null)}
    >
     {packageFetchError}
    </Alert>
   )}
  </Stack>
 ) : undefined;

 return (
  <Modal
   isOpen={isOpen}
   onClose={onClose}
   title={methodId ? "Edit Shipping Method" : "New Shipping Method"}
   size="lg"
   tabs={
    <Tabs
     tabs={tabs}
     tabsKey="shipping-form-tabs"
     sticky={true}
     renderHeadersOnly={true}
     activeTab={activeTabIndex}
     onTabChange={setActiveTabIndex}
    />
   }
   errors={modalErrors}
   footer={
    <div className="flex gap-2">
     <Button type="submit" form="shipping-form" loading={loading}>
      {methodId ? t("common.update") : t("common.create")}
     </Button>
     <Button type="button" variant="outline" onClick={onClose}>
      {t("common.cancel")}
     </Button>
    </div>
   }
  >
   <form id="shipping-form" onSubmit={handleSubmit}>
    <Stack spacing="md">
     {/* Render active tab content */}
     {tabs[activeTabIndex] && (
      <div>{tabs[activeTabIndex].content}</div>
     )}
    </Stack>
   </form>
  </Modal>
 );
};

